import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import { handleError } from "../../utils/handleError";

const initialState = {
	isLoading: true,
	error: null,
	folders:null,
	message: null,
	files: null,
	selectedFile: null,
	pagination: null,
};

const filesBackupSlice = createSlice({
	name: 'filesBackup',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearMessage: (state) => {
			state.message = null;
		},
		clearSelectedFile: (state) => {
			state.selectedFile = null;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAllFilesByQuery.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchAllFilesByQuery.fulfilled, (state, action) => {
				state.isLoading = false;
				state.files = action.payload?.data || [];
				const total = Number(action.payload?.total || 0);
				const page = Number(action.meta?.arg?.page || 1);
				const limit = Number(action.meta?.arg?.limit || 20);
				state.pagination = {
					page,
					limit,
					total,
					totalPages: Math.max(1, Math.ceil(total / limit)),
				};
				state.message = action.payload?.message || null;
			})
			.addCase(fetchAllFilesByQuery.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message || "Unable to fetch files.";
			})

			.addCase(deleteFilesBackup.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(deleteFilesBackup.fulfilled, (state, action) => {
				state.isLoading = false;
				const deletedIds = action.payload?.fileIds || [];
				if (Array.isArray(state.files) && deletedIds.length > 0) {
					state.files = state.files.filter((f) => !deletedIds.includes(f.fileId));
				}
				if (state.pagination) {
					const deletedCount = Number(action.payload?.deletedCount || deletedIds.length || 0);
					const nextTotal = Math.max(0, Number(state.pagination.total || 0) - deletedCount);
					state.pagination.total = nextTotal;
					state.pagination.totalPages = Math.max(1, Math.ceil(nextTotal / Number(state.pagination.limit || 20)));
				}
				state.message = action.payload?.message || `Deleted ${action.payload?.deletedCount || 0} backup record(s).`;
			})
			.addCase(deleteFilesBackup.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message || "Unable to delete file.";
			})
			.addCase(fetchAllFoldersByQuery.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchAllFoldersByQuery.fulfilled, (state, action) => {
				state.isLoading = false;
				state.folders = action.payload?.folders || [];
				state.pagination = action.payload?.pagination || null;
				state.message = action.payload?.message || null;
			})
			.addCase(fetchAllFoldersByQuery.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload?.message || "Unable to fetch folders.";
			})
	},
});


export const fetchAllFoldersByQuery = createAsyncThunk(
	'filesBackup/fetchAllFolders',
	async ({ page = 1, limit = 5, keyWord = "" }, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get(`/admin/files-backup/fetch-all-folders?page=${page}&limit=${limit}&keyWord=${keyWord}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);
export const fetchAllFilesByQuery = createAsyncThunk(
	'filesBackup/fetchAll',
	async ({ page = 1, limit = 5, keyWord = "" }, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.get(`/admin/files-backup/backup?page=${page}&limit=${limit}&keyWord=${keyWord}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const deleteFilesBackup = createAsyncThunk(
	'filesBackup/deleteMany',
	async ({ fileIds }, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.delete(`/admin/files-backup/backup/remove`, {
				data: { fileIds },
			});
			return {
				...res.data,
				fileIds,
			};
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

// Backward-compatible single delete helper
export const deleteFile = createAsyncThunk(
	'filesBackup/delete',
	async (fileId, { dispatch, rejectWithValue }) => {
		try {
			const result = await dispatch(deleteFilesBackup({ fileIds: [fileId] })).unwrap();
			return result;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);
export const deleteFolder = createAsyncThunk(
	'filesBackup/folders/removeFolder',
	async (folderId, { rejectWithValue }) => {
		try {
			const res = await axiosInstance.delete(`/admin/files-backup/folders/remove-folder/${folderId}`);
			return res.data;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	}
);

export const { clearError, clearMessage, clearSelectedFile } = filesBackupSlice.actions;
export default filesBackupSlice.reducer;
